import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import VariationStatusChip from './VariationStatusChip';

/** What sets a variation's share of traffic: its weight in a FLEX group, its place in a STEP one. */
const describeVariation = ({ offerId, variationLogic, flex, step }) => {
  if (variationLogic === 'FLEX') return `ID ${offerId} · Flex ${flex}`;
  if (variationLogic === 'STEP') return `ID ${offerId} · Step ${step}`;
  return `ID ${offerId}`;
};

/**
 * The variations a primary offer's flow node counts, each with its live/paused state in the pill the
 * node draws. Names open the variation's own offer in a new tab, like the pane's other offer links,
 * and wrap rather than truncate: variations usually share the primary's name up to a suffix.
 *
 * The text and the pill render as divs on purpose. RightAside.css styles every <span> in the aside
 * as a section header (1.1rem, semibold, text colour) and gives every <p> heading margins; a span
 * pill there loses its green/red text and border, and a caption balloons past the name above it.
 */
const OfferVariationsCard = ({ variations }) => {
  const sorted = useMemo(() => [...(variations ?? [])].sort((a, b) => a.offerId - b.offerId), [variations]);

  if (!sorted.length) return null;

  return (
    <Card>
      <CardHeader title='Offer Variations' slotProps={{ title: { component: 'h4' } }} />
      <List disablePadding>
        {sorted.map((variation, index) => (
          <ListItem key={variation.offerId} divider={index < sorted.length - 1} sx={{ gap: 1, px: 2 }}>
            <ListItemText
              primary={
                <Link
                  component={RouterLink}
                  to={`/campaigns/offertable/offer/${variation.offerId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  data-external-link='true'
                  underline='hover'
                  sx={{ display: 'block', fontWeight: 600, overflowWrap: 'anywhere' }}
                >
                  {variation.name}
                </Link>
              }
              secondary={describeVariation(variation)}
              slotProps={{
                primary: { variant: 'body2', component: 'div' },
                secondary: { variant: 'caption', component: 'div' },
              }}
              sx={{ minWidth: 0 }}
            />
            <VariationStatusChip component='div' isActive={variation.isActive} sx={{ flexShrink: 0 }}>
              {variation.isActive ? 'Active' : 'Paused'}
            </VariationStatusChip>
          </ListItem>
        ))}
      </List>
    </Card>
  );
};

export default OfferVariationsCard;
